import { inject, Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { OverlayDialogService } from '@services';
import { ErrorViewComponent } from 'app/components/error-view/error-view.component';
import { featureFlags } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

import { WINDOW } from '../../../core/window.token';
import { LoggerService } from '../../services/logger/logger.service';
import { UserSessionApiService } from '../../services/user-session-api/user-session-api.service';
import { UserDataService } from '../../services/userdata/userdata.service';

// Track if auth check has been performed this session
let authCheckCompleted = false;

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private userSessionApiService = inject(UserSessionApiService);
  private userDataService = inject(UserDataService);
  private overlayDialogService = inject(OverlayDialogService);
  private window = inject(WINDOW);
  private logger = inject(LoggerService).setContext('AuthGuard');

  async canActivate(): Promise<boolean> {
    // Skip if feature flag is disabled
    if (!featureFlags.enableUserSessionMonitoring) {
      this.logger.info('User session monitoring disabled via feature flag');
      return true;
    }

    // Skip if already checked this session
    if (authCheckCompleted) {
      this.logger.info('Auth already verified this session');
      return true;
    }

    let currentUrl = this.window.location.href;

    if (currentUrl.endsWith('/')) {
      currentUrl = currentUrl.slice(0, -1);
    }

    try {
      const httpResponse = await firstValueFrom(
        this.userSessionApiService.verifyAccountStatus(currentUrl)
      );

      if (!httpResponse) {
        this.logger.error('No response from verifyAccountStatus');
        this.overlayDialogService.open(ErrorViewComponent, {
          data: { option: 'something-went-wrong' }
        });
        return false;
      }

      // Extract body from HTTP response if using observe: 'response'
      const response = (httpResponse as any).body || httpResponse;

      if (!response) {
        this.logger.error('Empty response body');
        this.overlayDialogService.open(ErrorViewComponent, {
          data: { option: 'something-went-wrong' }
        });
        return false;
      }

      if (response.status === 'NOT_LOGGED_IN') {
        this.logger.warn('User not logged in, redirecting to login');
        this.userDataService.clearUserData();
        this.window.location.href = response.redirectUrl;
        return false;
      }

      // Mark auth check as completed
      authCheckCompleted = true;
      this.logger.info('Authentication verified successfully');
      return true;
    } catch (error) {
      this.logger.error('Exception during auth check:', error);
      this.overlayDialogService.open(ErrorViewComponent, {
        data: { option: 'something-went-wrong' }
      });
      return false;
    }
  }
}

// Export function to reset auth state (useful for testing or logout)
export const resetAuthGuard = () => {
  authCheckCompleted = false;
};
